import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, FolderKanban } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge, statusBadgeVariants } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Modal, ModalActions } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { useAuth } from '../../contexts/AuthContext';
import { getProjects, createProject, getAllClients } from '../../lib/supabase/queries';
import type { Project, Profile, ProjectStatus } from '../../types';

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'complete', label: 'Complete' },
];

export function ProjectsListPage() {
  const { profile, isAdmin } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Profile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // New project form
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    client_id: '',
    status: 'discovery' as ProjectStatus,
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const projectsData = await getProjects(isAdmin ? undefined : profile?.id);
        setProjects(projectsData);

        if (isAdmin) {
          const clientsData = await getAllClients();
          setClients(clientsData);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    }

    fetchData();
  }, [profile?.id, isAdmin]);

  const handleCreateProject = async () => {
    if (!newProject.title || !newProject.client_id) return;

    setIsCreating(true);
    try {
      const project = await createProject({
        title: newProject.title,
        description: newProject.description || null,
        client_id: newProject.client_id,
        status: newProject.status,
      });

      if (project) {
        setProjects([project, ...projects]);
        setShowCreateModal(false);
        setNewProject({
          title: '',
          description: '',
          client_id: '',
          status: 'discovery',
        });
      }
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Filter projects
  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-obsidian dark:text-signal">
            Projects
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            {isAdmin
              ? 'Manage all client projects'
              : 'Track your active projects'}
          </p>
        </div>
        {isAdmin && (
          <Button
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            New Project
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Select
          options={statusOptions}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-48"
        />
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card variant="bordered" padding="lg">
          <CardContent className="text-center py-12">
            <FolderKanban className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-obsidian dark:text-signal">
              No projects found
            </h3>
            <p className="mt-1 text-slate-500 dark:text-slate-400">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : isAdmin
                ? 'Create your first project to get started'
                : 'Your projects will appear here'}
            </p>
            {isAdmin && !searchQuery && statusFilter === 'all' && (
              <Button
                className="mt-4"
                onClick={() => setShowCreateModal(true)}
              >
                Create Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProjects.map((project) => (
            <Link key={project.id} to={`/dashboard/projects/${project.id}`}>
              <Card variant="bordered" padding="md" hover className="h-full">
                <CardContent>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <Badge variant={statusBadgeVariants[project.status] || 'default'}>
                      {project.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <h3 className="text-lg font-semibold text-obsidian dark:text-signal mb-2">
                    {project.title}
                  </h3>
                  {project.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4">
                      {project.description}
                    </p>
                  )}
                  {isAdmin && project.client && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                      Client: {project.client.company_name || 'Unknown'}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    Updated {format(new Date(project.updated_at), 'MMM d, yyyy')}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Project"
        description="Start a new project for a client"
      >
        <div className="space-y-4">
          <Input
            label="Project Title"
            value={newProject.title}
            onChange={(e) =>
              setNewProject({ ...newProject, title: e.target.value })
            }
            placeholder="e.g., Churn Prediction Model"
            required
          />
          <Textarea
            label="Description"
            value={newProject.description}
            onChange={(e) =>
              setNewProject({ ...newProject, description: e.target.value })
            }
            placeholder="Brief description of the project scope..."
          />
          <Select
            label="Client"
            value={newProject.client_id}
            onChange={(e) =>
              setNewProject({ ...newProject, client_id: e.target.value })
            }
            options={clients.map((c) => ({
              value: c.id,
              label: c.company_name || c.id,
            }))}
            placeholder="Select a client"
          />
          <Select
            label="Initial Status"
            value={newProject.status}
            onChange={(e) =>
              setNewProject({
                ...newProject,
                status: e.target.value as ProjectStatus,
              })
            }
            options={[
              { value: 'discovery', label: 'Discovery' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'review', label: 'Review' },
              { value: 'complete', label: 'Complete' },
            ]}
          />
        </div>
        <ModalActions>
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            isLoading={isCreating}
            disabled={!newProject.title || !newProject.client_id}
          >
            Create Project
          </Button>
        </ModalActions>
      </Modal>
    </div>
  );
}

export default ProjectsListPage;
